import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToGetData, hostURL, formatName } from "../../services/data_requests";
import {
  ArtworkCard,
  ArtistCard,
  Preloader,
  FilterComponent,
  Slider2,
} from "../../components";
import "./MarketPage.scss";
import "@fortawesome/fontawesome-free/css/all.min.css";
import FeaturedArtworks from "../../components/Common/Slider/FeaturedArtworks/FeaturedArtworks";
import { icon6 } from "../../assets";

const MarketPage = ({ marketType }) => {
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    categories: "All",
    medium: "All",
    price: "All",
    collection: "All",
    country: "All",
  });
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);
  const limit = 24;
  const HERO_SLIDE_INTERVAL = 10000;

  const [heroImages, setHeroImages] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const {
    blockItems,
    loading: fetchLoading,
    totalCount,
  } = useToGetData(marketType, limit, offset, 1000, selectedFilters);

  const totalPages = Math.ceil(totalCount / limit);

  const fetchFilters = async () => {
    try {
      const endpoint =
        marketType === "artwork"
          ? `${hostURL}artwork/filter_data`
          : marketType === "nfts"
            ? `${hostURL}nft/filter_data`
            : `${hostURL}artist/filter_data`;

      const response = await axios.get(endpoint);
      const filterKeyMap = {
        collections: "collection",
        countries: "country",
      };

      const normalizeFilterKeys = (filters) => {
        const normalizedFilters = {};
        Object.entries(filters).forEach(([key, value]) => {
          const normalizedKey = filterKeyMap[key] || key;
          normalizedFilters[normalizedKey] = value;
        });
        return normalizedFilters;
      };

      const normalizedFilters = normalizeFilterKeys(response.data);
      const fetchedFilters = {
        ...normalizedFilters,
        ...(marketType !== "artist" && {
          price: ["Under 1K USD", "Under 5K USD", "Under 10K USD"],
        }),
      };
      setFilters(fetchedFilters);
      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error fetching filters:", error);
      setLoading(false);
    }
  };

  const fetchData = async (newOffset) => {
    if (fetchLoading) {
      return;
    }
    setLoading(true);
    try {
      if (blockItems && blockItems.length >= 0) {
        let updatedItems = [...blockItems];
        if (searchQuery.trim()) {
          updatedItems = updatedItems.filter((item) =>
            item.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
          );
        }
        setMarketData(updatedItems);
        setOffset(newOffset);
        const hasMore = newOffset + limit < totalCount;
        setHasMoreData(hasMore);
      } else {
        setMarketData([]);
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setHasMoreData(false);
    } finally {
      setLoading(false);
      setIsNextLoading(false);
      setIsPrevLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, [marketType]);

  useEffect(() => {
    if (isDataLoaded) {
      fetchData(0);
    }
  }, [isDataLoaded]);
useEffect(() => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}, [offset]);
  useEffect(() => {
    if (blockItems !== undefined) {
      fetchData(offset);
    }
  }, [blockItems, searchQuery, selectedFilters]);

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((prev) => {
      const updatedFilters = { ...prev, [filterType]: value };
      if (filterType === "Print" && value !== "all") {
        updatedFilters.Painting = value;
        updatedFilters.Print = "All";
      } else if (filterType === "price") {
        updatedFilters.price = value;
      }
      if (filterType === "categories") {
        updatedFilters.categories = value;
      }
      if (filterType === "Painting") {
        updatedFilters.medium = value;
      }
      setOffset(0);
      setMarketData([]);
      return updatedFilters;
    });
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      categories: "All",
      medium: "All",
      price: "All",
      collection: "All",
      country: "All",
    });
    setSearchQuery("");
    setOffset(0);
    setMarketData([]);
  };

  const handleNextPage = () => {
    if (hasMoreData && !loading) {
      setIsNextLoading(true);
      const newOffset = offset + limit;
      setOffset(newOffset);
    }
  };

  const handlePreviousPage = () => {
    if (offset >= limit && !loading) {
      setIsPrevLoading(true);
      const newOffset = offset - limit;
      setOffset(newOffset);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (!loading) {
      const newOffset = (pageNumber - 1) * limit;
      setOffset(newOffset);
    }
  };

  const currentPage = Math.floor(offset / limit) + 1;

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        endPage = 3;
      } else if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
      }

      if (startPage > 2) {
        pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  const renderCard = (data, index) => {
    if (data) {
      if (marketType === "nfts") {
        return <ArtworkCard type="nft" data={data} key={`nft-${index}`} full />;
      } else if (marketType === "artist") {
        return <ArtistCard data={data} key={`artist-${index}`} />;
      } else if (marketType === "artwork") {
        return (
          <ArtworkCard
            type={marketType}
            data={data}
            key={`art-${index}`}
            full
          />
        );
      }
    }
  };

  useEffect(() => {
    setSelectedFilters({
      categories: "All",
      medium: "All",
      price: "All",
      collection: "All",
      country: "All",
    });
    setMarketData([]);
    setOffset(0);
    setHasMoreData(true);
    setSearchQuery("");
    setIsDataLoaded(false);
    setFilters(null);
  }, [marketType]);

  useEffect(() => {
    setHeroIndex(0);
    if (marketType === "artist") {
      setHeroImages([]);
      return;
    }
    axios
      .get(`${hostURL}artwork?count=10&offset=0`)
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : res.data.items || [];
        setHeroImages(items.map((item) => item.prev_img_url).filter(Boolean));
      })
      .catch(() => setHeroImages([]));
  }, [marketType]);

  useEffect(() => {
    if (heroImages.length < 2) return;
    const interval = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroImages.length);
    }, HERO_SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [heroImages]);

  return (
    <div className="MarketPage">
      {marketType !== "artist" && heroImages.length > 0 && (
        <div className="market-hero">
          <img
            key={`hero-${heroIndex}`}
            src={heroImages[heroIndex]}
            alt=""
            className="market-hero-img fa-fade-in"
          />
        </div>
      )}
      {isDataLoaded && filters ? (
        <>
          <div className="marketCover">
            {marketType === "artist" ? (
              <Slider2
                apiURL={hostURL + "artwork?count=10&offset=0"}
                useAPI={true}
                buttonText="Discover a masterpiece"
                buttonIcon={icon6}
                manuallyFormatLink={(item) =>
                  `/artwork/${formatName(item.name)}-${item.id}`
                }
              />
            ) : (
              <FeaturedArtworks
                apiURL={`${hostURL}artwork?count=5&offset=0`}
                manuallyFormatLink={(item) =>
                  `/artwork/${formatName(item.name)}-${item.id}`
                }
                titleFormatter={(item) => item.name}
                subtitleFormatter={(item) => item.artist?.name}
                descriptionFormatter={(item) => item.description}
                useAPI={true}
              />
            )}
          </div>
          <div className="filters-container">
            <FilterComponent
              filters={filters}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              title={marketType.toUpperCase()}
            />
          </div>
          <div className="search-container">
            <i className="fa fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="art-card-container">
            {marketData.length > 0 ? (
              marketData.map((data, index) => renderCard(data, index))
            ) : loading ? (
              <div className="no-items">Loading...</div>
            ) : (
              <div className="no-items">
                No items found for the selected filters.
              </div>
            )}
          </div>

          <div className="market-pagination">
            <button
              className="market-page-button market-page-button--prev"
              onClick={handlePreviousPage}
              disabled={offset === 0 || loading}
            >
              {isPrevLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                "Previous"
              )}
            </button>

            <div className="market-page-numbers">
              {pageNumbers.length > 0 ? (
                pageNumbers.map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="ellipsis">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`market-page-button market-page-button--number ${
                        currentPage === page ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  ),
                )
              ) : (
                <span className="no-pages">No pages to display</span>
              )}
            </div>

            <button
              className="market-page-button market-page-button--next"
              onClick={handleNextPage}
              disabled={!hasMoreData || loading}
            >
              {isNextLoading ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                "Next"
              )}
            </button>
          </div>
        </>
      ) : (
        <Preloader />
      )}
      {loading && <div className="loading-spinner">Loading...</div>}
    </div>
  );
};

export default MarketPage;
